import { NavLink } from 'react-router'
import { useMe } from '@/lib/auth'
import { t } from '@/lib/text'
import { cn } from '@/lib/utils'
import { NAV_GROUPS, type RouteDef, visibleRoutes } from '../routes'
import { AppName } from './environment'

export function NavItem({ route, collapsed = false, onNavigate }: { route: RouteDef; collapsed?: boolean; onNavigate?: () => void }) {
  if (!route.nav) return null
  const Icon = route.nav.icon
  const label = t(route.nav.labelKey)
  return (
    <NavLink
      to={route.path}
      end={route.path === '/'}
      onClick={onNavigate}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors duration-150',
          'hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
          'max-md:min-h-11',
          isActive && 'bg-accent text-accent-foreground',
          collapsed && 'justify-center px-2',
        )
      }
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      <span className={cn('truncate', collapsed && 'sr-only')}>{label}</span>
    </NavLink>
  )
}

export function NavList({ collapsed = false, onNavigate }: { collapsed?: boolean; onNavigate?: () => void }) {
  const me = useMe()
  const routes = visibleRoutes(me.permissions)
  return (
    <div className="flex flex-col gap-4">
      {NAV_GROUPS.map((group) => {
        const items = routes.filter((r) => r.nav?.group === group)
        if (!items.length) return null
        return (
          <div key={group} className="flex flex-col gap-1">
            {group !== 'main' && (
              <p className={cn('px-3 text-xs font-medium text-muted-foreground', collapsed && 'sr-only')}>{t(`core.nav.${group}`)}</p>
            )}
            <ul className="flex flex-col gap-1">
              {items.map((route) => (
                <li key={route.path}>
                  <NavItem route={route} collapsed={collapsed} onNavigate={onNavigate} />
                </li>
              ))}
            </ul>
          </div>
        )
      })}
    </div>
  )
}

export function Sidebar({ collapsed }: { collapsed: boolean }) {
  return (
    <aside
      className={cn(
        'sticky top-0 z-20 flex h-dvh shrink-0 flex-col border-r bg-sidebar text-sidebar-foreground max-md:hidden',
        collapsed ? 'w-16' : 'w-60',
      )}
    >
      <div className={cn('flex h-14 items-center border-b px-4 font-semibold', collapsed && 'justify-center px-2')}>
        <AppName className={cn('flex min-w-0 items-center', collapsed && 'sr-only')} />
      </div>
      <nav aria-label={t('core.nav.label')} className="flex-1 overflow-y-auto p-2">
        <NavList collapsed={collapsed} />
      </nav>
    </aside>
  )
}
